import { getErrorMessage } from "@/utils/error";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { TRANSACTION_ORDER_TYPE_LABELS } from "creso-core/constants";
import { useDeletePlan } from "creso-core/hooks";
import {
  Client,
  Plan,
  PlansWithPlanType,
  PlanType,
  SIPPlan,
  STPPlan,
  SWPPlan,
} from "creso-core/types";
import { format } from "date-fns";
import { ArrowDown, ChevronDownIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  V2Dialog,
  VisuallyHidden,
} from "web-design-system";

type Props = PlansWithPlanType & {
  client: Client;
  open: boolean;
  toggle: (show: boolean) => void;
};

type PlanItemProps = {
  plan: Plan;
  planType: PlanType;
};

function PlanName({ plan, planType }: PlanItemProps) {
  if (planType === "XSIP") {
    return (
      <span className="text-sm line-clamp-2">
        {(plan as SIPPlan).scheme_name}
      </span>
    );
  }
  if (planType === "STP") {
    return (
      <div className="flex flex-col">
        <span className="text-sm mb-2 line-clamp-1">
          {(plan as STPPlan).switch_out_scheme_name}
        </span>
        <ArrowDown />
        <span className="text-sm mt-2 line-clamp-1">
          {(plan as STPPlan).switch_in_scheme_name}
        </span>
      </div>
    );
  }
  if (planType === "SWP") {
    return (
      <span className="text-sm mt-2 line-clamp-2">
        {(plan as SWPPlan).scheme_name}
      </span>
    );
  }
  return null;
}

function PlanStats({ plan }: PlanItemProps) {
  return (
    <>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Amount</span>
        <span className="text-sm font-semibold">{plan.installment_amount}</span>
      </div>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Next installment</span>
        <span className="text-sm font-semibold">
          {format(new Date(plan.installment_due_date as string), "dd MMM yyyy")}
        </span>
      </div>
    </>
  );
}

export function DeleteOrderConfirmationDialog(props: Props) {
  const { mutateAsync: deleteOrder } = useDeletePlan(
    props.client.investment_account as string,
    props.planType
  );

  const { plans, planType } = props;

  const label = TRANSACTION_ORDER_TYPE_LABELS[planType];

  const [pendingPlans, setPendingPlans] = useState<Plan[]>(plans as Plan[]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (props.open) {
      setPendingPlans(plans as Plan[]);
      setErrors({});
    }
  }, [props.open, plans]);

  const isMultiple = pendingPlans.length > 1;

  const handleDelete = async () => {
    const total = pendingPlans.length;

    const results = await Promise.allSettled(
      pendingPlans.map((plan) =>
        deleteOrder({
          id: plan.id,
          investment_account_guid: props.client.investment_account as string,
        })
      )
    );

    const failedPlans: Plan[] = [];
    const nextErrors: Record<string, string> = {};
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const plan = pendingPlans[index];
        failedPlans.push(plan);
        nextErrors[plan.id.toString()] = getErrorMessage(
          result.reason,
          `Failed to delete ${label}`
        );
      }
    });

    if (failedPlans.length === 0) {
      toast.success(
        `${label}${plans.length > 1 ? "s" : ""} deleted successfully`
      );
      props.toggle(false);
      return;
    }

    setPendingPlans(failedPlans);
    setErrors(nextErrors);
    toast.error(
      `Failed to delete ${failedPlans.length} of ${total} ${label}${total > 1 ? "s" : ""}`
    );
  };

  return (
    <V2Dialog open={props.open} onOpenChange={props.toggle}>
      <DialogContent className="p-6 gap-6 space-y-0 w-120 max-w-none">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle className="text-lg">Delete {label}</DialogTitle>
          </VisuallyHidden>
          <div className="flex flex-col gap-4">
            <div className="text-lg font-semibold">
              Are you sure you want to delete{" "}
              {isMultiple
                ? `these ${pendingPlans.length} ${label}s`
                : `this ${label}`}
              ?
            </div>
            <div className="bg-border h-px w-full" />
            <div className="flex flex-col px-4 bg-card rounded-lg divide-y divide-border max-h-80 overflow-y-auto">
              {pendingPlans.map((plan) => {
                const error = errors[plan.id.toString()];
                return (
                  <Collapsible
                    key={plan.id}
                    defaultOpen={pendingPlans.length === 1}
                    className="group/collapsible py-4 flex flex-col"
                  >
                    <CollapsibleTrigger className="flex flex-row min-h-10 items-center gap-3 justify-between w-full text-left">
                      <div className="flex flex-row gap-3 items-center">
                        <AmcLogo
                          size="sm"
                          amc_code={plan.amc_morningstar_code}
                        />
                        <PlanName plan={plan} planType={planType} />
                      </div>
                      <ChevronDownIcon className="size-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="flex flex-row w-full mt-5">
                        <PlanStats plan={plan} planType={planType} />
                      </div>
                    </CollapsibleContent>
                    {error && (
                      <span className="text-xs text-destructive mt-2">
                        {error}
                      </span>
                    )}
                  </Collapsible>
                );
              })}
            </div>
            <p className="text-sm text-muted-foreground">
              The {label}
              {isMultiple ? "s" : ""} will be deleted and the user will not be
              able to view {isMultiple ? "them" : "it"} anymore. This action
              cannot be undone.
            </p>
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => props.toggle(false)}>
            No
          </Button>
          <Button onClick={handleDelete}>Yes, Delete</Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
